import {gql} from '@apollo/client';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import MoleculeChemicalStructure, {
  MoleculeChemicalStructureSize,
} from 'chemistry/molecules/tabs/MoleculeChemicalStructure';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Tag} from 'folder-items/FileConstants';
import {NucleotideType, SequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible as isInaccessibleInfo} from 'graphql-client/InaccessibleInfo';
import TagValueViewer from 'metadata/TagValueViewer';
import {
  BioentityPopoverContentQuery,
  BioentityPopoverContentQuery_bioentity as BioentityNode,
} from 'molbio/__generated/BioentityPopoverContentQuery';
import {BIOENTITY_TYPENAME_TO_TYPE} from 'registry/graphql/GraphQLBioentityTypes';
import {
  getNonRegistryIdentifierAliasInfos,
  getPrimaryRegistryIdentifier,
} from 'registry/RegistryIdentifierUtil';
import {ValidationExtras} from 'schemas/SchemaValidationConstants';
import {getTagSchemaTypeName} from 'tag-schema/util/TagSchemaUtil';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {BioentityChip} from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipPopoverContent from 'ui-library/chips/ChipPopoverContent';
import {generateKeyForSchemaField} from 'ui-library/chips/ChipPopoverContentUtils';
import {getIconForBioentity} from 'ui-library/chips/getIconForBioentity';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Link from 'ui-library/Link';
import {readableTime} from 'util/DateUtil';
import {typeToName} from 'util/NameUtil';
import {assertNever} from 'util/Types';

export const BIOENTITY_POPOVER_CONTENT_QUERY = gql`
  query BioentityPopoverContentQuery($id: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    bioentity(id: $id) {
      ...BioentityChipOrInaccessibleGraphQL
      ... on SequenceNode {
        id
        versionsJson
      }
      ... on ProteinNode {
        id
        versionsJson
      }
    }
  }
  ${getFragmentWrapper('BioentityChipOrInaccessible').fragment}
`;

interface VersionJson {
  id: number;
  created_at: string;
}

export interface BioentityPopoverContentProps {
  /** The API identifier of the bioentity. */
  id?: string | null;
  /** If provided, the Name field on the popover will become a link, which will call this function when
   * clicked.
   */
  onClickName?: () => void;
  versionId?: number;
  /** Information about the schema validation status of the bioentity. */
  validationExtras: ValidationExtras | null;
  showSummaryChipsAsPopover?: boolean;
}

function getVersionContent(versionId: number | undefined, bioentity: BioentityNode): string | null {
  // Versions only apply to sequences and proteins.
  if (
    versionId == null ||
    !(bioentity.__typename === 'SequenceNode' || bioentity.__typename === 'ProteinNode') ||
    bioentity.versionsJson == null
  ) {
    return null;
  }

  const version = bioentity.versionsJson.find((versionJson: VersionJson) => versionJson.id === versionId);
  if (version == null) {
    return null;
  }
  return readableTime(version.created_at);
}

function getSchemaTypeName(bioentity: BioentityNode): string {
  if (bioentity.__typename === 'SequenceNode') {
    switch (bioentity.sequenceType) {
      case SequenceType.ds:
        return typeToName(ModelTypeConstants.SEQUENCE);
      case SequenceType.ss:
        return typeToName(ModelTypeConstants.RNA_SEQUENCE);
      case SequenceType.oligo:
        if (bioentity.nucleotideType === NucleotideType.DNA) {
          return typeToName(ModelTypeConstants.DNA_OLIGO);
        } else {
          return typeToName(ModelTypeConstants.RNA_OLIGO);
        }
      default:
        assertAndContinue(false, `Unknown sequence schema type: ${assertNever(bioentity.sequenceType)}`);
    }
  }
  return getTagSchemaTypeName({
    folderItemType: BIOENTITY_TYPENAME_TO_TYPE[bioentity.__typename],
  });
}

/**
 * A component that queries for and formats some common information about a bioentity, to be displayed in a
 * popover.
 */
export function BioentityPopoverContent(props: BioentityPopoverContentProps): JSX.Element {
  return (
    <BenchlingQuery
      query={BIOENTITY_POPOVER_CONTENT_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
      variables={{id: props.id}}
    >
      {(data: BioentityPopoverContentQuery) => {
        if (isInaccessibleInfo(data.bioentity)) {
          return <>(No access)</>;
        }
        const schemaTypeName = getSchemaTypeName(data.bioentity);
        const versionContent = getVersionContent(props.versionId, data.bioentity);
        const {convertedToItem} = data.bioentity;
        const icon = getIconForBioentity(data.bioentity);

        const nameContent = (
          <>
            {icon ? <BenchlingIcon icon={icon} /> : null}
            &nbsp;{data.bioentity.name}
          </>
        );

        const fields = [
          {
            label: 'Name',
            value:
              props.onClickName != null ? (
                <Link
                  onClick={() => {
                    props.onClickName!();
                  }}
                  dataTestAttrs={{component: 'BioentityChipPopoverContent', element: 'entityNameLink'}}
                >
                  {nameContent}
                </Link>
              ) : (
                nameContent
              ),
          },
          {
            label: 'Version',
            value: versionContent,
          },
          {
            label: 'Converted to',
            value: convertedToItem ? (
              <BioentityChip
                id={convertedToItem.id}
                size={ChipSize.SMALL}
                validationExtras={props.validationExtras}
              />
            ) : null,
          },
          {
            label: 'Schema',
            value: data.bioentity.tagSchema ? `${data.bioentity.tagSchema.name} (${schemaTypeName})` : null,
          },
          {
            label: 'Type',
            value: data.bioentity.tagSchema == null ? schemaTypeName : null,
          },
          {
            label: 'Registry ID',
            value: getPrimaryRegistryIdentifier(data.bioentity),
          },
          {
            label: 'Aliases',
            value: getNonRegistryIdentifierAliasInfos(data.bioentity)
              .map((bai) => bai.alias)
              .join(', '),
          },
          {
            label: 'Project',
            value: data.bioentity.projectFolder && (
              <>
                <BenchlingIcon icon={ModelTypeConstants.PROJECT} />
                &nbsp;{data.bioentity.projectFolder.name}
              </>
            ),
          },
          {
            label: 'Authors',
            value: data.bioentity.authors ? (
              <>
                {data.bioentity.authors.map((author) => (
                  <span key={author.handle}>
                    <Avatar entity={author} size={AvatarSize.XSMALL} />
                    &nbsp;{author.name}
                  </span>
                ))}
              </>
            ) : null,
          },
          {label: 'Created', value: readableTime(data.bioentity.createdAt)},
          ...(BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE
            ? [
                {
                  label: 'Chemical Structure',
                  value:
                    BIOENTITY_TYPENAME_TO_TYPE[data.bioentity.__typename] === ModelTypeConstants.MOLECULE ? (
                      <MoleculeChemicalStructure
                        moleculeId={data.bioentity.id}
                        size={MoleculeChemicalStructureSize.FULL_SIZE}
                      />
                    ) : null,
                },
              ]
            : []),
          ...(data.bioentity.tagsJson as Array<Tag>).map((tag, index) => ({
            generateStableKey: () => `${generateKeyForSchemaField(tag)}_${index}`,
            label: tag.name,
            value: (
              <TagValueViewer
                tag={tag}
                chipSize={ChipSize.MINI}
                shouldPreviewImages={true}
                showChipsAsLinks={true}
                summarize={true}
                summarizeThreshold={3}
                showSummaryChipsAsPopover={props.showSummaryChipsAsPopover}
              />
            ),
          })),
        ];

        return <ChipPopoverContent info={{fields}} />;
      }}
    </BenchlingQuery>
  );
}
